<script lang="ts" setup>
import type { SizeVariants } from '~kui/types/kui';
import type { EmblaOptionsType } from 'embla-carousel';

import KUICoreCarouselArrow from '~kui/components/KUI/Core/Carousel/KUICoreCarouselArrow.vue';
import KUICoreCarouselDots from '~kui/components/KUI/Core/Carousel/KUICoreCarouselDots.vue';
import { useCarousel } from '~kui/composables/useCarousel';
import { useCarouselPlugins } from '~kui/composables/useCarouselPlugins';
import { useI18n } from 'vue-i18n';

import type { CarouselAutoplayOptions, CarouselAutoScrollOptions } from './types';

import {
  getCarouselContainerClasses,
  getCarouselSlideClasses,
  getCarouselSlideClassesAutoplayVariant,
  getCarouselViewportClasses,
  getCarouselWrapperClasses,
} from './carouselClassList';
import {
  CAROUSEL_DEFAULTS,
  resolveAutoplayOptions,
} from './types';

interface Props {
  // Autoplay options
  autoplay?: boolean | CarouselAutoplayOptions;
  autoScroll?: boolean | CarouselAutoScrollOptions;

  // UI controls
  showArrows?: boolean;
  arrowsPosition?: 'outside' | 'bottom' | 'left';
  showDots?: boolean;
  showAutoplayToggle?: boolean;
  dotsSize?: Extract<SizeVariants, 'sm' | 'md' | 'lg'>;
  dotsVariant?: 'default' | 'pill';

  // Core carousel options
  loop?: boolean;
  align?: 'start' | 'center' | 'end';
  slidesToScroll?: number | 'auto';
  duration?: number;
  dragFree?: boolean;
  options?: EmblaOptionsType;

  // Custom classes
  className?: string;
  containerClassName?: string;
  slideClassName?: string | string[]; // class to apply to all slides or an array of classes for each slide (to allow different classes per slide)
  viewportClassName?: string;
}

const props = withDefaults(defineProps<Props>(), {
  autoplay: false, // snap-based jumps
  autoScroll: false, // pixel-by-pixel scrolling

  showArrows: CAROUSEL_DEFAULTS.showArrows,
  arrowsPosition: CAROUSEL_DEFAULTS.arrowsPosition,
  showDots: CAROUSEL_DEFAULTS.showDots,
  showAutoplayToggle: true,
  dotsSize: CAROUSEL_DEFAULTS.dotsSize,
  dotsVariant: 'default',

  loop: CAROUSEL_DEFAULTS.loop,
  align: CAROUSEL_DEFAULTS.align,
  slidesToScroll: CAROUSEL_DEFAULTS.slidesToScroll,
  duration: CAROUSEL_DEFAULTS.duration,

  className: CAROUSEL_DEFAULTS.className,
  containerClassName: CAROUSEL_DEFAULTS.containerClassName,
  slideClassName: CAROUSEL_DEFAULTS.slideClassName,
  viewportClassName: CAROUSEL_DEFAULTS.viewportClassName,

  dragFree: CAROUSEL_DEFAULTS.dragFree,
});

const emit = defineEmits<{
  slideChange: [index: number];
}>()

// Resolve autoplay options
const autoplayOptions = computed(() => resolveAutoplayOptions(props.autoplay));
const isAutoplayEnabled = computed(() => autoplayOptions.value !== false);

// Build embla options
const emblaOptions = computed(() => {
  // Different breakpoint behavior for autoplay vs standard
  if (isAutoplayEnabled.value) {
    // Autoplay variant: simpler breakpoints, always 1 slide scroll
    return {
      loop: props.loop,
      align: props.align,
      duration: props.duration,
      slidesToScroll: 1,
      breakpoints: {
        '(min-width: 768px)': {
          slidesToScroll: 1,
        },
        '(min-width: 1024px)': {
          slidesToScroll: 1,
          align: 'start' as const,
        },
      },
    } as EmblaOptionsType;
  }

  // Standard variant: full responsive breakpoints
  return { ...{
    loop: props.loop,
    align: props.align,
    duration: props.duration,
    slidesToScroll: props.slidesToScroll,
    dragFree: props.dragFree,
    breakpoints: {
      '(min-width: 768px)': {
        slidesToScroll: props.slidesToScroll,
      },
      '(min-width: 1024px)': {
        slidesToScroll: props.slidesToScroll,
        align: 'start' as const,
      },
    },
  } as EmblaOptionsType, ...props.options };
});

// Build plugins
const emblaPlugins = computed(() => {
  const pluginOptions: Parameters<typeof useCarouselPlugins>[0] = {};

  if (isAutoplayEnabled.value) {
    pluginOptions.autoplay = autoplayOptions.value as CarouselAutoplayOptions;
  }

  pluginOptions.autoScroll = props.autoScroll;

  return useCarouselPlugins(pluginOptions);
});

// Initialize carousel
const {
  emblaRef,
  emblaApi,
  selectedIndex,
  scrollSnaps,
  canScrollPrev,
  canScrollNext,
  slides,
  scrollPrev,
  scrollNext,
  scrollTo,
} = useCarousel({
  options: emblaOptions,
  plugins: emblaPlugins,
});

const isPlaying = ref(true);

const toggleAutoplay = () => {
  if (!emblaApi.value) {
    return;
  }
  const autoplayPlugin = emblaApi.value.plugins()?.autoplay;
  if (!autoplayPlugin) {
    return;
  }
  if (isPlaying.value) {
    autoplayPlugin.stop();
  }
  else {
    autoplayPlugin.play();
  }
  isPlaying.value = !isPlaying.value;
};

watch(selectedIndex, (newIndex) => {
  emit('slideChange', newIndex)
})

defineExpose({
  scrollTo,
  scrollPrev,
  scrollNext,
  selectedIndex,
  scrollSnaps,
})

// Computed classes
const wrapperClasses = computed(() =>
  getCarouselWrapperClasses({
    showArrows: props.showArrows,
    className: props.className,
    arrowsPosition: props.arrowsPosition,
  }),
);

const viewportClasses = computed(() =>
  getCarouselViewportClasses({
    viewportClassName: props.viewportClassName,
  }),
);

const containerClasses = computed(() =>
  getCarouselContainerClasses({
    containerClassName: props.containerClassName,
  }),
);

const slideClasses = computed(() => {
  // handle String version of slideClassName (ie all the slides have the same class)
  if (!props.slideClassName || !Array.isArray(props.slideClassName)) {
    return slides.value.map(() =>
      isAutoplayEnabled.value
        ? getCarouselSlideClassesAutoplayVariant({
            slideClassName: props.slideClassName as string | undefined,
          })
        : getCarouselSlideClasses({
            slideClassName: props.slideClassName as string | undefined,
          }),
    );
  }

  // handle Array version of slideClassName
  return props.slideClassName?.map(className => getCarouselSlideClasses({
    slideClassName: className,
  }));
});

// Determine aria-label based on autoplay
const { t } = useI18n();

const ariaLabel = computed(() => {
  return t('unified.common.carousel.navigation');
});
</script>

<template>
  <div
    :class="wrapperClasses"
    role="region"
    :aria-label="ariaLabel"
    aria-roledescription="carousel"
  >
    <!-- Embla Viewport -->
    <div ref="emblaRef" :class="viewportClasses">
      <div :class="containerClasses">
        <div
          v-for="(slide, index) in slides"
          :key="`embla-slide-${isAutoplayEnabled ? 'autoplay' : 'standard'}-${index}`"
          :class="slideClasses[index]"
          role="group"
          :aria-roledescription="t('unified.common.carousel.slide')"
          :aria-label="t('unified.common.carousel.slideOf', { current: index + 1, total: slides.length })"
        >
          <component :is="slide" />
        </div>
      </div>

      <slot name="viewport-post" />
    </div>

    <div
      class="sr-only"
      aria-live="polite"
      aria-atomic="true"
    >
      {{ t('unified.common.carousel.slideOf', { current: selectedIndex + 1, total: scrollSnaps.length }) }}
    </div>

    <!-- Bottom arrows with dots: arrows flanking dots in a single row, frosted glass overlay -->
    <div
      v-if="arrowsPosition === 'bottom' && showDots && (showArrows || showDots) && scrollSnaps.length > 1"
      class="absolute inset-x-0 bottom-0 z-10 flex items-center justify-center gap-2 bg-white/60 py-1.5 backdrop-blur-md"
    >
      <KUICoreCarouselArrow
        v-if="showArrows"
        direction="left"
        :disabled="!canScrollPrev"
        @click="scrollPrev"
      />
      <KUICoreCarouselDots
        :total="scrollSnaps.length"
        :active-index="selectedIndex"
        :size="dotsSize"
        :variant="dotsVariant"
        @select="scrollTo"
      />
      <button
        v-if="isAutoplayEnabled && showAutoplayToggle"
        type="button"
        class="rounded-full p-1 text-xs focus-visible:ring-2 focus-visible:ring-foreground"
        :aria-label="isPlaying ? t('unified.common.carousel.pauseCarousel') : t('unified.common.carousel.playCarousel')"
        @click="toggleAutoplay"
      >
        {{ isPlaying ? '\u23F8' : '\u25B6' }}
      </button>
      <KUICoreCarouselArrow
        v-if="showArrows"
        direction="right"
        :disabled="!canScrollNext"
        @click="scrollNext"
      />
    </div>

    <!-- Bottom arrows without dots -->
    <div
      v-else-if="arrowsPosition === 'bottom' && showArrows && (canScrollPrev || canScrollNext)"
      class="full-w flex justify-center mt-6"
    >
      <KUICoreCarouselArrow
        direction="left"
        :disabled="!canScrollPrev"
        @click="scrollPrev"
      />
      <KUICoreCarouselArrow
        direction="right"
        :disabled="!canScrollNext"
        @click="scrollNext"
      />
    </div>

    <!-- Outside arrows -->
    <template v-else-if="arrowsPosition === 'outside' && showArrows && (canScrollPrev || canScrollNext)">
      <div class="absolute top-1/2 left-0 z-10 -translate-y-1/2">
        <KUICoreCarouselArrow
          direction="left"
          :disabled="!canScrollPrev"
          @click="scrollPrev"
        />
      </div>
      <div class="absolute top-1/2 right-0 z-10 -translate-y-1/2">
        <KUICoreCarouselArrow
          direction="right"
          :disabled="!canScrollNext"
          @click="scrollNext"
        />
      </div>
    </template>

    <!-- Left arrows -->
    <template v-else-if="arrowsPosition === 'left' && showArrows && (canScrollPrev || canScrollNext)">
      <div class="absolute top-1/2 left-0 z-10 -translate-y-1/2">
        <KUICoreCarouselArrow
          direction="left"
          :disabled="!canScrollPrev"
          @click="scrollPrev"
        />
      </div>
      <div class="absolute top-1/2 left-16 z-10 -translate-y-1/2">
        <KUICoreCarouselArrow
          direction="right"
          :disabled="!canScrollNext"
          @click="scrollNext"
        />
      </div>
    </template>

    <!-- Dots Navigation (standalone, outside position) -->
    <div
      v-if="arrowsPosition === 'outside' && showDots && scrollSnaps.length > 1"
      class="mt-6 flex items-center justify-center gap-2"
    >
      <KUICoreCarouselDots
        :total="scrollSnaps.length"
        :active-index="selectedIndex"
        :size="dotsSize"
        :variant="dotsVariant"
        @select="scrollTo"
      />
      <button
        v-if="isAutoplayEnabled && showAutoplayToggle"
        type="button"
        class="rounded-full p-1 text-xs focus-visible:ring-2 focus-visible:ring-foreground"
        :aria-label="isPlaying ? t('unified.common.carousel.pauseCarousel') : t('unified.common.carousel.playCarousel')"
        @click="toggleAutoplay"
      >
        {{ isPlaying ? '\u23F8' : '\u25B6' }}
      </button>
    </div>
  </div>
</template>
